/** Modal do Portal: isolamento, ciclo de foco e devolução ao acionador (AB-032). */
import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

function focaveis(painel: HTMLElement): HTMLElement[] {
  return Array.from(painel.querySelectorAll<HTMLElement>('button, a[href], input, select, textarea, [tabindex]'))
    .filter(el => el.tabIndex >= 0 && !el.matches(':disabled') && el.getClientRects().length > 0)
}

export function ModalDoPortal({ rotulo, aoFechar, teste, testeDoVeu, largo, children }: {
  rotulo: string
  aoFechar: () => void
  teste: string
  testeDoVeu?: string
  /** 672px em vez de 512px: o modal que tem tabela, como o `max-w-2xl` do app interno (comissoes.tsx:560). */
  largo?: boolean
  children: ReactNode
}) {
  const veu = useRef<HTMLDivElement>(null)
  const painel = useRef<HTMLDivElement>(null)
  const fechar = useRef(aoFechar)
  fechar.current = aoFechar
  useEffect(() => {
    const modal = painel.current
    if (modal === null) return
    const acionador = document.activeElement
    const marcados: HTMLElement[] = []
    for (const irmao of Array.from(document.body.children)) {
      if (irmao instanceof HTMLElement && irmao !== veu.current && !irmao.inert) {
        irmao.inert = true
        marcados.push(irmao)
      }
    }
    const rolagem = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const porTecla = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') { ev.preventDefault(); fechar.current(); return }
      if (ev.key !== 'Tab') return
      const alvos = focaveis(modal)
      const primeiro = alvos[0]
      const ultimo = alvos.at(-1)
      if (primeiro === undefined) { ev.preventDefault(); modal.focus(); return }
      if (ev.shiftKey && (document.activeElement === primeiro || document.activeElement === modal)) {
        ev.preventDefault(); ultimo?.focus()
      } else if (!ev.shiftKey && (document.activeElement === ultimo || document.activeElement === modal)) {
        ev.preventDefault(); primeiro.focus()
      }
    }
    document.addEventListener('keydown', porTecla)
    ;(focaveis(modal)[0] ?? modal).focus()
    return () => {
      document.removeEventListener('keydown', porTecla)
      for (const irmao of marcados) irmao.inert = false
      document.body.style.overflow = rolagem
      if (acionador instanceof HTMLElement && acionador.isConnected) acionador.focus()
    }
  }, [])
  return createPortal(
    <div className="veu" ref={veu} role="presentation" data-teste={testeDoVeu} onMouseDown={aoFechar}>
      <div className={largo === true ? 'modal modal-largo' : 'modal'} ref={painel} role="dialog" aria-modal="true" aria-label={rotulo}
        tabIndex={-1} data-teste={teste} onMouseDown={ev => { ev.stopPropagation() }}>
        {children}
      </div>
    </div>, document.body,
  )
}
